import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Section, Field, Input, SaveButton } from "./ProjectSettings";
import { AssetViewer } from "./AssetViewer";
import { useRepositoryHome, useRepositoryList } from "../api/hooks";
import type { InboxAssetVM } from "../api/types/repository";

type RepoTab = "prds" | "inbox" | "settings";

export function RepoHome({
  repositoryId,
  repoSlug,
  tab: initialTab,
}: {
  repositoryId: Id<"repositories">;
  repoSlug: string;
  tab: RepoTab;
}) {
  const [tab, setTab] = useState<RepoTab>(initialTab);
  const repoHome = useRepositoryHome(repositoryId);

  const switchTab = (t: RepoTab) => {
    setTab(t);
    window.location.hash = `/${encodeURIComponent(repoSlug)}/${t}`;
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-1 border-b border-zinc-800 mb-6">
        <button
          onClick={() => switchTab("inbox")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === "inbox"
              ? "border-indigo-500 text-indigo-400"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          PRD Inbox
        </button>
        <button
          onClick={() => switchTab("prds")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === "prds"
              ? "border-indigo-500 text-indigo-400"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          PRD Executions
        </button>
        <button
          onClick={() => switchTab("settings")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === "settings"
              ? "border-indigo-500 text-indigo-400"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Settings
        </button>
      </div>

      {tab === "prds" && <PrdList repositoryId={repositoryId} repoSlug={repoSlug} repoHome={repoHome} />}
      {tab === "inbox" && <InboxTab repositoryId={repositoryId} repoSlug={repoSlug} repoHome={repoHome} />}
      {tab === "settings" && <RepoSettings repositoryId={repositoryId} repoSlug={repoSlug} />}
    </div>
  );
}

function InboxTab({ repositoryId, repoSlug, repoHome }: {
  repositoryId: Id<"repositories">;
  repoSlug: string;
  repoHome: ReturnType<typeof useRepositoryHome>;
}) {
  // In the schema, the `projects` table stores what the UI calls "PRD Executions".
  const projects = repoHome.status === "ready" ? repoHome.data.projects : [];
  const assetBadges: Record<string, { label: string; href: string }> = {};
  for (const p of projects) {
    if (p.prdSourceAssetId) {
      assetBadges[p.prdSourceAssetId] = {
        label: `PRD Execution: ${p.name}`,
        href: `#/${encodeURIComponent(repoSlug)}/${encodeURIComponent(p.name)}/graph`,
      };
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-500">
        Assets uploaded without a linked PRD execution. Link one as a PRD source from the <strong>PRD Executions</strong> tab.
      </p>
      <AssetViewer repositoryId={repositoryId} assetBadges={assetBadges} />
    </div>
  );
}

function PrdList({ repositoryId, repoSlug, repoHome }: {
  repositoryId: Id<"repositories">;
  repoSlug: string;
  repoHome: ReturnType<typeof useRepositoryHome>;
}) {
  const archiveProject = useMutation(api.projects.archive);
  const [showArchived, setShowArchived] = useState(false);

  if (repoHome.status === "loading") {
    return <div className="text-zinc-600 text-sm py-4">Loading...</div>;
  }
  if (repoHome.status === "error") {
    return <div className="text-red-400 text-sm py-4">Error: {repoHome.error}</div>;
  }

  const { projects, inboxAssets } = repoHome.data;
  const sorted = [...projects].sort((a, b) => b.createdAt - a.createdAt);
  const archivedCount = sorted.filter((p) => p.archived).length;
  const visible = showArchived ? sorted : sorted.filter((p) => !p.archived);
  return (
    <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-zinc-300">
            {visible.length} PRD Execution{visible.length !== 1 ? "s" : ""}
          </h3>
          {archivedCount > 0 && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              {showArchived ? "Hide" : "Show"} archived ({archivedCount})
            </button>
          )}
        </div>

        {visible.length === 0 ? (
          <p className="text-sm text-zinc-600 py-8 text-center">
            {archivedCount > 0
              ? "All PRD executions are archived. Toggle above to view them."
              : "No PRD executions in this project yet."}
          </p>
        ) : (
          <div className="space-y-1.5">
            {visible.map((project) => {
              const linkedElsewhere = new Set(
                sorted
                  .filter((p) => p.id !== project.id && p.prdSourceAssetId)
                  .map((p) => p.prdSourceAssetId!)
              );
              const availableInbox = inboxAssets.filter((a) => !linkedElsewhere.has(a.id));
              return (
                <PrdRow
                  key={project.id}
                  project={project}
                  repoSlug={repoSlug}
                  inboxAssets={availableInbox}
                  allInboxAssets={inboxAssets}
                  onArchive={(archived) => void archiveProject({ projectId: project.id as Id<"projects">, archived })}
                />
              );
            })}
          </div>
        )}
    </div>
  );
}

function PrdRow({
  project,
  repoSlug,
  inboxAssets,
  allInboxAssets,
  onArchive,
}: {
  project: {
    id: string;
    name: string;
    createdAt: number;
    archived: boolean;
    prdSourceAssetId?: string;
  };
  repoSlug: string;
  inboxAssets: InboxAssetVM[];
  allInboxAssets: InboxAssetVM[];
  onArchive: (archived: boolean) => void;
}) {
  const linkPrdAsset = useMutation(api.projects.linkPrdAsset);
  // Look up linked asset filename from all inbox assets (no separate query needed)
  const linkedAssetName = project.prdSourceAssetId
    ? allInboxAssets.find((a) => a.id === project.prdSourceAssetId)?.fileName
    : undefined;
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const link = async (assetId: string | null) => {
    setMenuOpen(false);
    setBusy(true);
    try {
      await linkPrdAsset({ projectId: project.id as Id<"projects">, assetId: (assetId ?? undefined) as Id<"assets"> | undefined });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`flex items-center justify-between bg-zinc-900 border rounded-md px-4 py-3 gap-3 group ${
        project.archived
          ? "border-zinc-800/50 opacity-60"
          : "border-zinc-800 hover:border-zinc-700"
      }`}
    >
      <button
        onClick={() => {
          window.location.hash = `/${encodeURIComponent(repoSlug)}/${encodeURIComponent(project.name)}/graph`;
        }}
        className="flex flex-col gap-0.5 text-left min-w-0 flex-1"
      >
        <span className="text-sm font-medium text-zinc-200 truncate">{project.name}</span>
        <span className="text-xs text-zinc-600">
          {new Date(project.createdAt).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </span>
      </button>

      <div className="shrink-0 relative">
        {project.prdSourceAssetId ? (
          <div className="flex items-center gap-2">
            <span
              className="text-xs text-zinc-400 font-mono max-w-[180px] truncate bg-zinc-800/60 border border-zinc-700 rounded px-2 py-1"
              title={linkedAssetName ?? "Linked asset"}
            >
              {linkedAssetName ?? "..."}
            </span>
            <button
              onClick={() => void link(null)}
              disabled={busy}
              className="text-[11px] text-zinc-500 hover:text-red-400 transition-colors disabled:opacity-40"
              title="Unlink PRD source"
            >
              Unlink
            </button>
          </div>
        ) : (
          <>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              disabled={busy}
              className="text-xs text-indigo-400 hover:text-indigo-300 border border-indigo-500/30 rounded px-2 py-1 disabled:opacity-40"
            >
              Link inbox asset
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl py-1 min-w-[220px] max-h-64 overflow-y-auto">
                  {inboxAssets.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-zinc-500">No unassigned inbox assets.</div>
                  ) : (
                    inboxAssets.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => void link(a.id)}
                        className="w-full text-left px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-700 hover:text-white flex items-center gap-2"
                      >
                        <span className="text-[10px] font-bold text-zinc-500 bg-zinc-900 px-1.5 py-0.5 rounded">
                          INPUT
                        </span>
                        <span className="truncate">{a.fileName}</span>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <button
        onClick={() => onArchive(!project.archived)}
        className="text-xs text-zinc-600 hover:text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
        title={project.archived ? "Unarchive" : "Archive"}
      >
        {project.archived ? "Unarchive" : "Archive"}
      </button>
    </div>
  );
}

function RepoSettings({ repositoryId, repoSlug }: { repositoryId: Id<"repositories">; repoSlug: string }) {
  const repoList = useRepositoryList();

  if (repoList.status === "loading") {
    return <div className="text-zinc-600 text-sm py-4">Loading...</div>;
  }
  if (repoList.status === "error") {
    return <div className="text-red-400 text-sm py-4">Error: {repoList.error}</div>;
  }

  const repo = repoList.data.find((r) => r.id === repositoryId);
  if (!repo) {
    return <div className="text-zinc-600 text-sm py-4">Repository not found.</div>;
  }

  return (
    <div className="space-y-6">
      <RepoFields repo={{ _id: repo.id as Id<"repositories">, name: repo.name, slug: repo.slug, repoPath: repo.repoPath }} />
      <RepoDangerZone repo={{ _id: repo.id as Id<"repositories">, name: repo.name }} />
    </div>
  );
}

function RepoFields({
  repo,
}: {
  repo: { _id: Id<"repositories">; name: string; slug: string; repoPath: string };
}) {
  const updateRepo = useMutation(api.repositories.update);
  const [name, setName] = useState(repo.name);
  const [repoPath, setRepoPath] = useState(repo.repoPath);
  const [saving, setSaving] = useState(false);

  const dirty = name !== repo.name || repoPath !== repo.repoPath;

  const save = async () => {
    setSaving(true);
    try {
      await updateRepo({
        repositoryId: repo._id,
        ...(name !== repo.name ? { name } : {}),
        ...(repoPath !== repo.repoPath ? { repoPath } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Project">
      <Field label="Name">
        <Input value={name} onChange={setName} placeholder="Project name" />
      </Field>
      <Field label="Slug">
        <span className="text-sm text-zinc-400 font-mono">{repo.slug}</span>
        <span className="text-[11px] text-zinc-600">Auto-derived from name on save</span>
      </Field>
      <Field label="Repo Path">
        <Input value={repoPath} onChange={setRepoPath} placeholder="/path/to/repo" mono />
      </Field>
      <div className="flex justify-end">
        <SaveButton dirty={dirty} saving={saving} onClick={() => void save()} />
      </div>
    </Section>
  );
}

function RepoDangerZone({
  repo,
}: {
  repo: { _id: Id<"repositories">; name: string };
}) {
  const removeRepo = useMutation(api.repositories.remove);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await removeRepo({ repositoryId: repo._id });
      window.location.hash = "/";
      window.location.reload();
    } catch {
      setDeleting(false);
    }
  };

  return (
    <div className="border border-red-500/20 rounded-lg">
      <div className="px-4 py-3 border-b border-red-500/20">
        <h3 className="text-sm font-medium text-red-400">Danger Zone</h3>
      </div>
      <div className="px-4 py-4 space-y-3">
        <p className="text-sm text-zinc-400">
          Permanently delete project <strong className="text-zinc-200">{repo.name}</strong> and unlink all its PRD executions. This cannot be undone.
        </p>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={`Type "${repo.name}" to confirm`}
            className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-red-500 flex-1"
          />
          <button
            onClick={() => void handleDelete()}
            disabled={confirmText !== repo.name || deleting}
            className="px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-red-600 hover:bg-red-500 text-white"
          >
            {deleting ? "Deleting..." : "Delete project"}
          </button>
        </div>
      </div>
    </div>
  );
}
